import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Download, Maximize2, X, Loader2 } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';

// Set worker path
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

const PDFViewer = ({ url, fileName, onClose, embedded = false }) => {
  const canvasRef = useRef(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.2);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rendering, setRendering] = useState(false);

  // Load PDF document
  useEffect(() => {
    const loadPDF = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const loadingTask = pdfjsLib.getDocument({
          url: url,
          cMapUrl: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/cmaps/',
          cMapPacked: true,
        });
        
        const pdf = await loadingTask.promise;
        setPdfDoc(pdf);
        setTotalPages(pdf.numPages);
        setLoading(false);
      } catch (err) {
        console.error('Error loading PDF:', err);
        setError('Failed to load PDF. The file may be corrupted or inaccessible.');
        setLoading(false);
      }
    };

    if (url) {
      loadPDF();
    }

    return () => {
      if (pdfDoc) {
        pdfDoc.destroy();
      }
    };
  }, [url]);

  // Render current page
  useEffect(() => {
    const renderPage = async () => {
      if (!pdfDoc || !canvasRef.current || rendering) return;

      try {
        setRendering(true);
        const page = await pdfDoc.getPage(currentPage);
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        const viewport = page.getViewport({ scale });
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        const renderContext = {
          canvasContext: context,
          viewport: viewport,
        };

        await page.render(renderContext).promise;
        setRendering(false);
      } catch (err) {
        console.error('Error rendering page:', err);
        setRendering(false);
      }
    };

    renderPage();
  }, [pdfDoc, currentPage, scale]);

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.2, 3));
  };

  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.2, 0.5));
  };

  const openFullscreen = () => {
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className={`flex flex-col items-center justify-center ${embedded ? 'h-64' : 'h-96'} bg-gray-50 rounded-xl`}>
        <Loader2 className="w-8 h-8 text-purple-600 animate-spin mb-3" />
        <p className="text-sm text-gray-500">Loading PDF...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex flex-col items-center justify-center ${embedded ? 'h-64' : 'h-96'} bg-red-50 rounded-xl border border-red-200`}>
        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-3">
          <X className="w-6 h-6 text-red-500" />
        </div>
        <p className="text-sm text-red-600 text-center px-4">{error}</p>
        <a 
          href={url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="mt-3 text-sm text-purple-600 hover:underline flex items-center gap-1"
        >
          <Download size={14} /> Download instead
        </a>
      </div>
    );
  }

  return (
    <div className={`bg-gray-900 rounded-xl overflow-hidden ${embedded ? '' : 'shadow-2xl'}`}>
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-300 truncate max-w-[200px]" title={fileName}>
            {fileName || 'Document.pdf'}
          </span>
        </div>
        
        <div className="flex items-center gap-1">
          {/* Page Navigation */}
          <button
            onClick={goToPrevPage}
            disabled={currentPage <= 1}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Previous page"
          >
            <ChevronLeft size={18} />
          </button>
          <span className="text-sm text-gray-300 px-2 min-w-[80px] text-center">
            {currentPage} / {totalPages}
          </span>
          <button
            onClick={goToNextPage}
            disabled={currentPage >= totalPages}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Next page"
          >
            <ChevronRight size={18} />
          </button>
          
          <div className="w-px h-5 bg-gray-700 mx-2" />
          
          {/* Zoom Controls */}
          <button
            onClick={zoomOut}
            disabled={scale <= 0.5}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded disabled:opacity-30 transition-colors"
            title="Zoom out"
          >
            <ZoomOut size={18} />
          </button>
          <span className="text-xs text-gray-400 min-w-[50px] text-center">
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={zoomIn}
            disabled={scale >= 3}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded disabled:opacity-30 transition-colors"
            title="Zoom in"
          >
            <ZoomIn size={18} />
          </button>
          
          <div className="w-px h-5 bg-gray-700 mx-2" />
          
          {/* Actions */}
          <button
            onClick={openFullscreen}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
            title="Open in new tab"
          >
            <Maximize2 size={18} />
          </button>
          <a
            href={url}
            download={fileName}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
            title="Download"
          >
            <Download size={18} />
          </a>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors ml-1"
              title="Close"
            >
              <X size={18} />
            </button>
          )}
        </div>
      </div>
      
      {/* Canvas Container */}
      <div className={`overflow-auto bg-gray-800 ${embedded ? 'max-h-[400px]' : 'max-h-[70vh]'}`}>
        <div className="flex justify-center p-4 min-h-[300px]">
          {rendering && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-900/50 z-10">
              <Loader2 className="w-6 h-6 text-white animate-spin" />
            </div>
          )}
          <canvas 
            ref={canvasRef} 
            className="shadow-lg"
            style={{ maxWidth: '100%', height: 'auto' }}
          />
        </div>
      </div>
    </div>
  );
};

export default PDFViewer;
